import React from 'react';
import { motion } from 'framer-motion';
import {
  Scan,
  Settings,
  Wrench,
  CircuitBoard,
  Battery,
  Droplets,
  HardHat,
  Activity,
  ArrowRight,
  CheckCircle,
  Zap
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { FirebaseImage } from '../FirebaseImage';

const MechanicalServicesSection: React.FC = () => {
  const { t, translations } = useLanguage();

  const mechanicalServices = [
    {
      id: 'oilChange',
      icon: Droplets,
      title: t('home.mechanicalServices.services.oilChange.title'),
      description: t('home.mechanicalServices.services.oilChange.description'),
      features: translations.home?.mechanicalServices?.services?.oilChange?.features || [],
      image: '/Vidange-Moteur.jpg',
      color: 'from-red-600 to-red-700'
    },
    {
      id: 'maintenance',
      icon: Activity,
      title: t('home.mechanicalServices.services.maintenance.title'),
      description: t('home.mechanicalServices.services.maintenance.description'),
      features: translations.home?.mechanicalServices?.services?.maintenance?.features || [],
      image: '/Revision-Entretien.jpg',
      color: 'from-black to-gray-800'
    },
    {
      id: 'diagnostic',
      icon: Scan,
      title: t('home.mechanicalServices.services.diagnostic.title'),
      description: t('home.mechanicalServices.services.diagnostic.description'),
      features: translations.home?.mechanicalServices?.services?.diagnostic?.features || [],
      image: '/Diagnostic-electronique.jpg',
      color: 'from-red-500 to-red-600'
    },
    {
      id: 'brakes',
      icon: CircuitBoard,
      title: t('home.mechanicalServices.services.brakes.title'),
      description: t('home.mechanicalServices.services.brakes.description'),
      features: translations.home?.mechanicalServices?.services?.brakes?.features || [],
      image: '/Systeme-freinage.jpg',
      color: 'from-black to-gray-900'
    }
  ];

  const advantages = translations.home?.mechanicalServices?.advantages?.items || [];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-900 via-black to-gray-800 text-white overflow-hidden relative">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, #E30613 0%, transparent 50%),
                           radial-gradient(circle at 75% 75%, #E30613 0%, transparent 50%)`
        }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.div
            animate={{ 
              rotate: [0, 360],
              scale: [1, 1.1, 1]
            }}
            transition={{ 
              duration: 20,
              repeat: Infinity,
              ease: "linear"
            }}
            className="inline-block mb-6"
          >
            <div className="w-20 h-20 bg-gradient-to-r from-red-600 to-red-500 rounded-full flex items-center justify-center shadow-2xl">
              <Wrench className="w-10 h-10 text-white" />
            </div>
          </motion.div>

          <h2 className="text-4xl md:text-6xl font-bold mb-6">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-red-600">
              {t('home.mechanicalServices.title')}
            </span>
          </h2>
          
          <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            {t('home.mechanicalServices.subtitle')}
          </p>
          
          <div className="w-32 h-1 bg-gradient-to-r from-red-600 to-red-500 mx-auto mt-8 rounded-full" />
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {mechanicalServices.map((service, index) => {
            const IconComponent = service.icon;
            
            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group relative"
              >
                {/* Card */}
                <motion.div
                  whileHover={{ y: -10, scale: 1.02 }}
                  className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl overflow-hidden h-full shadow-2xl hover:shadow-red-500/20 transition-all duration-300 border border-gray-700 hover:border-red-500/50"
                >
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <FirebaseImage
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent" />
                    
                    {/* Icon Overlay */}
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      className={`absolute top-4 right-4 w-12 h-12 rounded-xl bg-gradient-to-r ${service.color} flex items-center justify-center shadow-lg`}
                    >
                      <IconComponent className="w-6 h-6 text-white" />
                    </motion.div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-3 group-hover:text-red-400 transition-colors">
                      {service.title}
                    </h3>
                    
                    <p className="text-gray-400 mb-4 leading-relaxed">
                      {service.description}
                    </p>

                    {/* Features */}
                    <ul className="space-y-2">
                      {service.features.map((feature, featureIndex) => (
                        <motion.li
                          key={featureIndex}
                          initial={{ opacity: 0, x: -10 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.1 + featureIndex * 0.1 }}
                          className="flex items-center space-x-2 text-sm text-gray-300"
                        >
                          <CheckCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                          <span>{feature}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>
                </motion.div>

                {/* Hover Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-red-600/0 via-red-600/5 to-red-600/0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
              </motion.div>
            );
          })}
        </div>

        {/* Advantages Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="bg-gradient-to-r from-red-600/10 to-red-700/10 rounded-3xl p-12 backdrop-blur-sm border border-red-600/20"
        >
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold mb-4">{t('home.mechanicalServices.advantages.title')}</h3>
            <p className="text-gray-300 text-lg">{t('home.mechanicalServices.advantages.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {advantages.map((advantage, index) => {
              const iconComponents = [Settings, Zap, Wrench];
              const IconComponent = iconComponents[index] || Settings;
              
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                  className="text-center group"
                >
                  <motion.div
                    whileHover={{ scale: 1.2, rotate: 10 }}
                    className="w-16 h-16 bg-gradient-to-r from-red-600 to-red-700 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:shadow-red-500/50 transition-shadow duration-300"
                  >
                    <IconComponent className="w-8 h-8 text-white" />
                  </motion.div>
                  
                  <h4 className="text-xl font-bold mb-2 group-hover:text-red-400 transition-colors">
                    {advantage.title}
                  </h4>
                  
                  <p className="text-gray-400">
                    {advantage.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-center mt-16"
        >
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <motion.a
              href="/services"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center space-x-3 px-8 py-4 bg-gradient-to-r from-red-600 to-red-700 text-white font-semibold rounded-lg hover:from-red-700 hover:to-red-800 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <span>{t('home.mechanicalServices.cta.primary')}</span>
              <motion.div
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              >
                <ArrowRight className="w-5 h-5" />
              </motion.div>
            </motion.a>

            <motion.a
              href="/contact"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center space-x-3 px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-gray-900 transition-all duration-200"
            >
              <span>{t('home.mechanicalServices.cta.secondary')}</span>
              <ArrowRight className="w-5 h-5" />
            </motion.a>
          </div>
        </motion.div>

        {/* Floating Elements */}
        <motion.div
          animate={{ 
            y: [0, -20, 0],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{ 
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-20 right-10 w-6 h-6 bg-red-500 rounded-full opacity-30"
        />
        
        <motion.div
          animate={{ 
            y: [0, 20, 0],
            opacity: [0.4, 0.7, 0.4]
          }}
          transition={{ 
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1.5
          }}
          className="absolute bottom-20 left-10 w-4 h-4 bg-red-400 rounded-full opacity-40"
        />
      </div>
    </section>
  );
};

export default MechanicalServicesSection;